import { FileUp } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import FilePickerButton from '~/components/FilePickerButton'
import { Button } from '~/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '~/components/ui/dialog'
import UploadProgress from '~/components/UploadProgress'
import { documentForm, titleFromFileName } from './document-form'
import DocumentFields from './DocumentFields'
import { useDocumentUpload } from './use-document-upload'

type IProps = {
  sessions: readonly { id: string, name: string }[]
  onCancel: () => void
  onUploaded: () => void
}

/** Déposer un document pour les apprenants (#326) : un PDF, son titre, son parcours, s'il est à accepter. */
function DocumentUploadDialog({ sessions, onCancel, onUploaded }: IProps) {
  const { t } = useTranslation()
  const [file, setFile] = useState<File | null>(null)
  const [form, setForm] = useState(() => documentForm())
  const upload = useDocumentUpload(onUploaded)

  const choose = (chosen: File) => {
    setFile(chosen)
    if (!form.title.trim()) setForm(current => ({ ...current, title: titleFromFileName(chosen.name) }))
  }

  const submit = (event: React.FormEvent) => {
    event.preventDefault()
    if (file && form.title.trim()) void upload.submit(file, form)
  }

  return (
    <Dialog open onOpenChange={open => !open && !upload.busy && onCancel()}>
      <DialogContent className="sm:max-w-lg">
        <form onSubmit={submit} className="flex flex-col gap-4">
          <DialogHeader>
            <DialogTitle>{t('documents.new.title')}</DialogTitle>
            <DialogDescription>{t('documents.new.description')}</DialogDescription>
          </DialogHeader>
          <div className="flex flex-wrap items-center gap-3">
            <FilePickerButton accept="application/pdf,.pdf" onFile={choose} disabled={upload.busy}>
              <FileUp className="mr-2 size-4" aria-hidden />
              {t('documents.new.pick')}
            </FilePickerButton>
            <span className="text-sm text-muted-foreground">{file?.name ?? t('documents.new.noFile')}</span>
          </div>
          <DocumentFields value={form} onChange={setForm} sessions={sessions} />
          {upload.state.phase !== 'idle' && <UploadProgress state={upload.state} processingLabel={t('documents.new.processing')} onCancel={upload.cancel} />}
          {upload.error && <p role="alert" className="text-sm text-error-text">{upload.error}</p>}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onCancel} disabled={upload.busy}>{t('common.cancel')}</Button>
            <Button type="submit" disabled={!file || !form.title.trim() || upload.busy}>{t('documents.new.submit')}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

export default DocumentUploadDialog
